// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect, useState } from "react";
import {
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Popper,
  Paper,
  ClickAwayListener,
  Fade,
  TableSortLabel,
  Button,
  Box,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { v4 as uuidv4 } from "uuid";
import { useSnackbar } from "notistack";
import {
  useGetUserPaginatedQuery,
  useDeleteUserMutation,
} from "../../redux/api/userApi";
import customTheme from "../../theme/customTheme";
import StyledTable from "../../theme/CustomTable/CustomTable";
import Styled from "../../theme/CustomMenu/customMenu";
import ConfirmationDialog from "../../components/Dialog/ConfirmationDialog";
import { useAppSelector } from "../../hooks/storeHooks";
import { setTableSeting } from "../../redux/settingSlice";
import useDebounce from "../../hooks/useDebounce";
import Search from "../../components/Search";
import AddEditUser from "./AddEditUser/AddEditUser";
import { setUserAuth } from "../../redux/userSlice";
import usePagination from "../../hooks/usePagination";

enum SortingDirectionEnum {
  ASCENDING = "asc",
  DESCENDING = "desc",
}

type SortingDirection = `${SortingDirectionEnum}`;

type HeadCell = {
  id: string;
  label: string;
  isSorting: boolean;
};

const headCells: readonly HeadCell[] = [
  {
    id: "username",
    label: "Username",
    isSorting: true,
  },

  {
    id: "email",
    label: "Email",
    isSorting: false,
  },
  {
    id: "firstName",
    label: "First name",
    isSorting: false,
  },

  {
    id: "lastname",
    label: "Last name",
    isSorting: false,
  },
  {
    id: "role",
    label: "Role",
    isSorting: false,
  }
];

export default function UserManagement({ tabValue }: { tabValue?: number }) {
  // for pagination
  const {
    rowsPerPage,
    page,
    setPage,
    handleChangeRowsPerPage,
    handleChangePage,
  } = usePagination();
  const [isOwnAccount, setIsOwnAccount] = useState(false);
  const userState = useAppSelector(setUserAuth);
  const { dense } = useAppSelector(setTableSeting);
  const [name, setName] = React.useState("");
  const debounceValue = useDebounce(name, 700);
  const [deleteUser] = useDeleteUserMutation();
  const [order, setOrder] = useState<SortingDirection>(
    SortingDirectionEnum.DESCENDING
  );
  const { data: Users, refetch } =
    useGetUserPaginatedQuery({ page, rowsPerPage, debounceValue, active: true }, { refetchOnMountOrArgChange: true })
  const [orderBy, setOrderBy] = useState("name");
  const [showDialog, setShowDialog] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState(false);
  const [rowId, setRowId] = useState("");

  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | any>(null);
  const { enqueueSnackbar } = useSnackbar();

  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  function getComparator(order: SortingDirection, orderBy: string) {
    return order === SortingDirectionEnum.ASCENDING
      ? (a: Record<string, unknown>, b: Record<string, unknown>) =>
        descendingComparator(a, b, orderBy)
      : (a: Record<string, unknown>, b: Record<string, unknown>) =>
        -descendingComparator(a, b, orderBy);
  }

  const handleCloseDialog = () => {
    setShowDialog(false);
    setRowId("");
    setOpen(false);
  };

  const handleDeleteDialog = () => {
    setConfirmationDialog(false);
  };

  const handleDeleteUser = () => {
    deleteUser(rowId)
      .unwrap()
      .then(() =>
        enqueueSnackbar("Success", {
          variant: "success",
          autoHideDuration: 2000,
        })
      )
      .catch(() =>
        enqueueSnackbar("Failed", {
          variant: "error",
          autoHideDuration: 2000,
        })
      );
    setConfirmationDialog(false);
    setRowId("");
  };


  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  const createSortHandler =
    (property: string) => (event: React.MouseEvent<unknown>) => {
      handleRequestSort(event, property);
    };

  const handleClose = () => {
    setOpen(false);
  };

  const handleContextMenu = (e: React.MouseEvent, rowId: string, userName: string) => {
    e.preventDefault();
    e.stopPropagation();
    const { clientX, clientY } = e;
    setOpen(true);
    setRowId(rowId);
    if (userName === userState?.user?.username) {
      setIsOwnAccount(true);
    } else {
      setIsOwnAccount(false);
    }
    const virtualElement = {
      getBoundingClientRect: () => ({
        width: 0,
        height: 0,
        top: clientY,
        right: clientX,
        bottom: clientY,
        left: clientX,
      }),
    };
    setAnchorEl(virtualElement);
  };
  const id = open ? "user-managment-menu" : undefined;

  // get background color of table row
  function getTableRowColor(
    rowId: string | number,
    selectedRowId: string | number | null
  ) {
    const highlightColor = customTheme.palette.secondary.main;
    const defaultColor = customTheme.palette.secondary.light;
    if (selectedRowId) {
      return selectedRowId === rowId ? highlightColor : defaultColor;
    }
    return defaultColor;
  }

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
    setPage(1);
  };

  useEffect(() => {
    if (tabValue === 0) {
      refetch();
    }
  }, [tabValue]);


  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button variant="contained" onClick={() => setShowDialog(true)}>
          <AddCircleIcon sx={{ marginRight: "0.5rem" }} /> Create User
        </Button>
        <Search name={name} handleSearch={handleSearch} />
      </Box>
      <StyledTable>
        <TableContainer>
          <Table
            aria-labelledby="tableTitle"
            size={dense ? "small" : "medium"}
            sx={dense ? { marginTop: "1rem" } : null}
          >
            <TableHead>
              <TableRow>
                {headCells.map((item) => (
                  <TableCell key={item.id}>
                    {item.isSorting ? (
                      <TableSortLabel
                        active={orderBy === `${item.id}`}
                        direction={orderBy === `${item.id}` ? order : "asc"}
                        onClick={createSortHandler(item.id)}
                      >
                        {item.label}
                      </TableSortLabel>
                    ) : (
                      item.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Users?.results &&
                Users?.results
                  ?.slice(rowsPerPage * 0, rowsPerPage * (page + 1))
                  ?.sort(getComparator(order, orderBy))
                  ?.map((data) => (
                    <TableRow
                      key={uuidv4()}
                      onContextMenu={(event) =>
                        handleContextMenu(event, data.id, data.username)
                      }
                      style={{
                        backgroundColor: getTableRowColor(data.id, rowId),
                      }}
                    >
                      <TableCell>{data?.username}</TableCell>
                      <TableCell>{data?.email}</TableCell>
                      <TableCell>{data?.first_name}</TableCell>
                      <TableCell>{data?.last_name}</TableCell>
                      <TableCell>{data?.roles[0]}</TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
      </StyledTable>
      <TablePagination
        rowsPerPageOptions={[20, 25, 50, 100]}
        component="div"
        count={Users?.count || 0}
        rowsPerPage={rowsPerPage}
        page={page - 1}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      {anchorEl && (
        <Popper
          id={id}
          open={open}
          anchorEl={anchorEl}
          transition
          placement="bottom-start"
        >
          {({ TransitionProps }) => (
            <ClickAwayListener onClickAway={handleClose}>
              <Fade {...TransitionProps}>
                <Paper>
                  <Styled>
                    <MenuItem onClick={() => setShowDialog(true)}>
                      <EditIcon />
                      Edit
                    </MenuItem>
                    <MenuItem
                      disabled={isOwnAccount}
                      onClick={() => setConfirmationDialog(true)}
                    >
                      <DeleteIcon />
                      Delete
                    </MenuItem>
                  </Styled>
                </Paper>
              </Fade>
            </ClickAwayListener>
          )}
        </Popper>
      )}

      {showDialog && (
        <AddEditUser
          isOpen={showDialog}
          handleCloseDialog={handleCloseDialog}
          id={rowId}
          isOwnAccount={isOwnAccount}
        />
      )}
      {confirmationDialog && rowId && (
        <ConfirmationDialog
          isOpen={confirmationDialog}
          onClose={handleDeleteDialog}
          onConfirm={() => handleDeleteUser()}
          title="Delete User"
          confirmText="Confirm"
          isWarning
        >
          Are you sure you want to delete User ?
        </ConfirmationDialog>
      )}
    </Box>
  );
}
